import { Alert, App, DatePicker, Form, Select, Spin } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { arrayRemove, arrayUnion } from "firebase/firestore"
import { useStore } from "jotai"
import { cloneDeep, last } from "lodash"
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"

import { classAtoms, classSessionAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import { NULL_ID } from "#classes/Firebase"
import SwitchClassSessionGrid from "#components/forms/AddStudent/SwitchClassSessionGrid"
import FormWithValues from "#components/FormWithValues"
import {
  ATTENDANCE_STATUSES,
  VALID_SWITCH_STATUSES,
} from "#constants/globalConstants"
import { useApiContext } from "#contexts/ApiContextProvider"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  ENROLLMENT_RECEIPT_TYPES,
  SwitchClassReceipt,
} from "#customTypes/composedTypes/emailTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseBatchUpdate,
  FirebaseBatchUpdateClass,
  FirebaseBatchUpdateClassSession,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { AttendanceStatus } from "#customTypes/types"
import SkillLevelDropdown from "#forms/SkillLevelDropdown"
import {
  useIsAllFulfilled,
  useIsFulfilled,
} from "#hooks/jotai/useCascadingData"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import {
  useFilteredDocs,
  useOneDoc,
  useSomeDocs,
  useStableIds,
} from "#hooks/jotai/useSubscription"
import { useFirebaseBatchUpdate } from "#hooks/reactQuery/useFirebaseMutation"
import { useErrorModal } from "#hooks/useErrorModal"
import { generateSessionId, generateSessionIds } from "#utils/classHelpers"
import {
  formatDob,
  getNow,
  STANDARD_DATE_FORMAT,
  STANDARD_DATE_TIME_FORMAT,
  STANDARD_DAY_FORMAT,
  UNKNOWN_DOB,
} from "#utils/dateHelpers"
import { getStudentRecipients } from "#utils/emailHelpers"
import {
  type AnySkillLevel,
  assertEnrollmentSkillLevel,
  buildSkillLevelStudentPatch,
  formatMissingSkillLevelMessage,
  getMissingSkillLevelForEnrollment,
} from "#utils/enrollmentSkillLevelHelpers"
import {
  getBlankAttendance,
  isStillEnrolled,
  remapAbsentForFutureSessions,
} from "#utils/studentHelpers"
import { ErrorType } from "#utils/textFormatters"

type SwitchClassFormValues = {
  switchDate: Dayjs
  newClassId: string
  /** Only present when the student lacks the new class group's level (JARVIS-43). */
  newSkillLevel?: AnySkillLevel
}

export type SwitchClassModalRef = {
  submit: () => Promise<void>
}

// Check if sessions have invalid attendance statuses for switching
const hasException = (
  attendanceSessions:
    | { status: AttendanceStatus; sessionDate?: dayjs.Dayjs }[]
    | undefined,
  isNewClass = false,
  getNow: () => dayjs.Dayjs
) => {
  if (!attendanceSessions) return false

  if (isNewClass) {
    // If student is already enrolled in the new class sessions, they cannot be switched
    // Use remapped status for future sessions
    const remappedSessions = attendanceSessions.map((session) => ({
      ...session,
      status: remapAbsentForFutureSessions(
        session.status,
        session.sessionDate || getNow(),
        getNow()
      ),
    }))
    return isStillEnrolled(remappedSessions)
  }

  // Old class: Block actual attendance statuses
  // Use remapped status for future sessions
  const remappedSessions = attendanceSessions.map((session) => ({
    ...session,
    status: remapAbsentForFutureSessions(
      session.status,
      session.sessionDate || dayjs(),
      getNow()
    ),
  }))
  return !remappedSessions.every(({ status }) =>
    VALID_SWITCH_STATUSES.includes(status)
  )
}

const SwitchClassModalContent = ({
  oldClassId,
  studentId,
  onSelectionChange,
  isAssistant,
}: {
  oldClassId: Uuid
  studentId: Uuid
  onSelectionChange: (selectedIndices: boolean[]) => void
  isAssistant: boolean
}) => {
  const form = Form.useFormInstance()

  const switchDate =
    Form.useWatch("switchDate", form) || getNow().startOf("day")
  const newClassId = Form.useWatch("newClassId", form)

  const student = useGetOneStudent(studentId)
  const oldClassAttendance = useMemo(() => {
    return (
      (isAssistant
        ? student?.assistantAttendance?.[oldClassId]
        : student?.attendance?.[oldClassId]) ?? []
    )
  }, [student, oldClassId, isAssistant])

  const newClassAttendance = useMemo(() => {
    if (!newClassId) return []

    return (
      (isAssistant
        ? student?.assistantAttendance?.[newClassId]
        : student?.attendance?.[newClassId]) ?? []
    )
  }, [student, newClassId, isAssistant])

  // Regular attendance is opposite of assistant attendance
  const newClassOppositeAttendance = useMemo(() => {
    if (!newClassId) return []

    return (
      (isAssistant
        ? student?.attendance?.[newClassId]
        : student?.assistantAttendance?.[newClassId]) ?? []
    )
  }, [student, newClassId, isAssistant])

  const oldClassObj = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    oldClassId
  )

  const stableOldSessionIds = useStableIds(oldClassObj?.sessionIds)
  const oldClassSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    stableOldSessionIds
  )

  const newClassObj = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    newClassId
  )

  const stableNewSessionIds = useStableIds(newClassObj?.sessionIds)
  const newClassSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    stableNewSessionIds
  )

  const isOldClassSessionsFulfilled = useIsFulfilled(
    stableOldSessionIds,
    oldClassSessions
  )

  const isNewClassSessionsFulfilled = useIsFulfilled(
    stableNewSessionIds,
    newClassSessions
  )

  const isAllFulfilled = useIsAllFulfilled([
    !!oldClassObj,
    isOldClassSessionsFulfilled,
    !!newClassObj,
    isNewClassSessionsFulfilled,
  ])

  // Get classes with the same curriculum
  const relatedClassesQueryConfig: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        curriculumId: {
          value: oldClassObj?.curriculumId ?? NULL_ID,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [oldClassObj?.curriculumId]
  )

  const relatedClasses = useFilteredDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    relatedClassesQueryConfig
  )

  const classOptions = useMemo(() => {
    if (!relatedClasses) return []

    return relatedClasses
      .sort((a, b) => {
        const aDate = dayjs(`${a.startDate} ${a.startTime}`)
        const bDate = dayjs(`${b.startDate} ${b.startTime}`)
        return aDate.diff(bDate)
      })
      .map(({ id, name }) => ({
        label: name,
        value: id,
      }))
      .filter(({ value }) => value !== oldClassObj?.id)
  }, [relatedClasses, oldClassObj?.id])

  // JARVIS-43: switching INTO a non-camp class is an enrollment, so the
  // student needs a level in that class group. TAs are exempt.
  const missingSkillLevel = useMemo(
    () =>
      isAssistant
        ? null
        : getMissingSkillLevelForEnrollment(student, newClassObj),
    [isAssistant, student, newClassObj]
  )

  // Always set to first option if no value is set, or if current value is not in options
  useLayoutEffect(() => {
    if (classOptions.length) {
      const currentValue = form.getFieldValue("newClassId")
      // Always set to first option if no value is set, or if current value is not in options
      const isValidValue = classOptions.some(
        (opt) => opt.value === currentValue
      )
      if (!currentValue || !isValidValue) {
        form.setFieldValue("newClassId", classOptions[0].value)
      }
    }
  }, [classOptions, form])

  return (
    <Flex col gap={20} style={{ width: "100%", maxWidth: 1200 }}>
      <Flex gap={20}>
        <Form.Item
          label="Switch date"
          name="switchDate"
          layout="vertical"
          rules={[{ required: true, message: "Please select a date" }]}
        >
          <DatePicker
            format="M/D/YY - dddd"
            style={{ width: 200 }}
            minDate={dayjs("2016-01-01")}
          />
        </Form.Item>
        <Form.Item
          label="Class"
          name="newClassId"
          layout="vertical"
          rules={[{ required: true, message: "Please select a class" }]}
          style={{ width: 300 }}
        >
          <Select
            options={classOptions}
            loading={
              relatedClasses.length === 0 && oldClassObj?.curriculumId != null
            }
            notFoundContent={
              relatedClasses.length === 0 && oldClassObj?.curriculumId != null
                ? "Loading classes..."
                : "No classes found"
            }
          />
        </Form.Item>
        {missingSkillLevel && (
          <div style={{ width: 260 }} data-testid="switch-class-skill-level">
            <SkillLevelDropdown
              fieldName="newSkillLevel"
              classGroup={missingSkillLevel.classGroup}
              required
            />
          </div>
        )}
      </Flex>
      {missingSkillLevel && (
        <Alert
          type="warning"
          showIcon
          message={formatMissingSkillLevelMessage(
            missingSkillLevel,
            student?.name
          )}
        />
      )}

      {/* Always show current class structure, show loading for new class if needed */}
      {isAllFulfilled ? (
        <SwitchClassSessionGrid
          studentId={studentId}
          oldClassAttendance={oldClassAttendance!}
          newClassAttendance={newClassAttendance!}
          newClassOppositeAttendance={newClassOppositeAttendance!}
          oldClassId={oldClassId}
          oldClassObj={oldClassObj!}
          oldClassSessions={oldClassSessions}
          newClassId={newClassId}
          newClassObj={newClassObj}
          newClassSessions={newClassSessions}
          switchDate={switchDate}
          onSelectionChange={onSelectionChange}
          isAssistant={isAssistant}
        />
      ) : (
        <Spin size="small" />
      )}
    </Flex>
  )
}

const SwitchClassModal = forwardRef<
  SwitchClassModalRef,
  {
    oldClassId: Uuid
    studentId: Uuid
    isAssistant: boolean
  }
>(({ oldClassId, studentId, isAssistant }, ref) => {
  const [switchClassForm] = Form.useForm<SwitchClassFormValues>()
  const [selectedIndices, setSelectedIndices] = useState<boolean[]>([])

  const { modal, message } = App.useApp()
  const { apiHandler } = useApiContext()
  const store = useStore()

  const openErrorModal = useErrorModal("Switching class")
  const { mutate: onSwitchClass } = useFirebaseBatchUpdate([
    DOC_TYPES.CLASS_SESSIONS,
    DOC_TYPES.STUDENTS,
    DOC_TYPES.CLASSES,
  ])

  const student = useGetOneStudent(studentId)
  const oldClassObj = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    oldClassId
  )

  const handleSubmit = useCallback(
    async (values: SwitchClassFormValues) => {
      try {
        const { newClassId, newSkillLevel } = values
        const newClassObj = store.get(classAtoms.docByIdAtom(newClassId))
        const sortedNewClassSessions = store.get(
          classSessionAtoms.docsByIdsAtom(newClassObj?.sessionIds ?? [])
        )
        const sortedOldClassSessions = store.get(
          classSessionAtoms.docsByIdsAtom(oldClassObj?.sessionIds ?? [])
        )

        const selectedCount = selectedIndices.filter(Boolean).length
        if (selectedCount === 0) {
          throw new Error(
            `Please select at least one session to switch ${isAssistant ? "assistant attendance" : "class"}.`
          )
        }

        // JARVIS-43: block a switch into a non-camp class when the student has
        // no level for its group, unless one was just chosen in the form.
        const skillLevelRequirement = isAssistant
          ? null
          : assertEnrollmentSkillLevel(student, newClassObj, newSkillLevel)
        const skillLevelPatch =
          skillLevelRequirement && newSkillLevel
            ? buildSkillLevelStudentPatch(
                student,
                skillLevelRequirement,
                newSkillLevel
              )
            : {}

        const { attendance, assistantAttendance } = student ?? {}

        const currentAttendance = isAssistant ? assistantAttendance : attendance
        const clonedAttendance = cloneDeep(currentAttendance) ?? {}
        const clonedOldClassAttendance = clonedAttendance[oldClassId] ?? []

        // Check opposite attendance type in new sessions:
        // - If switching assistant attendance: check if student is enrolled as a regular student
        // - If switching regular attendance: check if student is enrolled as a TA
        const oppositeAttendance = isAssistant
          ? attendance?.[newClassId]
          : assistantAttendance?.[newClassId]

        if (oppositeAttendance) {
          // Map opposite attendance to include session dates for remapping
          // Only check sessions that are selected
          const oppositeAttendanceWithDates = oppositeAttendance
            .map((att, index) => {
              if (!selectedIndices[index]) return null
              const session = sortedNewClassSessions[index]
              return {
                status: att.status,
                sessionDate: session ? dayjs(session.eventStart) : dayjs(),
              }
            })
            .filter(
              (
                att
              ): att is {
                status: AttendanceStatus
                sessionDate: dayjs.Dayjs
              } => att !== null
            )
          if (hasException(oppositeAttendanceWithDates, true, getNow)) {
            throw new Error(
              isAssistant
                ? "Student is already enrolled as a student in the new class. Remove them as a student before switching assistant attendance."
                : "Student is already a TA in the new class. Remove them as a TA before switching."
            )
          }
        }

        // Ensure old class attendance exists and is the right length
        const maxSessions = Math.max(
          clonedOldClassAttendance.length,
          sortedNewClassSessions.length
        )

        // This should never happen, but just in case
        if (!clonedAttendance[oldClassId]) {
          clonedAttendance[oldClassId] = getBlankAttendance(
            generateSessionIds(oldClassId, maxSessions)
          )
        }

        // Extend old class attendance array if needed
        if (clonedOldClassAttendance.length < maxSessions) {
          const existingLength = clonedOldClassAttendance.length
          const additionalSessionIds = Array.from(
            { length: maxSessions - existingLength },
            (_, i) => generateSessionId(oldClassId, existingLength + i)
          )
          clonedOldClassAttendance.push(
            ...getBlankAttendance(additionalSessionIds)
          )
        }

        // Create blank attendance record for new class if it doesn't exist
        if (!clonedAttendance[newClassId]) {
          const newSessionIds = generateSessionIds(newClassId, maxSessions)
          clonedAttendance[newClassId] = getBlankAttendance(newSessionIds)
        }

        const classSessionUpdate: Record<
          string,
          FirebaseBatchUpdateClassSession
        > = {}

        // Build database updates for selected sessions
        selectedIndices.forEach((isSelected, sessionIndex) => {
          if (!isSelected) return

          const oldSession = clonedOldClassAttendance[sessionIndex]
          if (!oldSession) {
            console.error(
              `SESSION MISSING for ${isAssistant ? "assistant" : "student"} switch`,
              sessionIndex,
              clonedOldClassAttendance
            )
          }
          const oldSessionId = oldSession?.sessionId

          // Remap ABSENT to ENROLLED for future sessions when checking validity
          const remappedStatus = remapAbsentForFutureSessions(
            oldSession.status,
            sortedOldClassSessions[sessionIndex]?.eventStart,
            getNow()
          )

          // Should never happen, but just in case
          if (!VALID_SWITCH_STATUSES.includes(remappedStatus)) {
            return
          }

          // Students/Assistants enter new class as ENROLLED
          const newSessionId = generateSessionId(newClassId, sessionIndex)
          clonedAttendance[newClassId][sessionIndex] = {
            status: ATTENDANCE_STATUSES.ENROLLED,
            sessionId: newSessionId,
          }

          // Set old class status to NOT_ENROLLED
          clonedAttendance[oldClassId][sessionIndex] = {
            status: ATTENDANCE_STATUSES.NOT_ENROLLED,
            sessionId: oldSessionId,
          }

          // Update class session studentIds or assistantIds based on isAssistant
          const sessionIdField = isAssistant ? "assistantIds" : "studentIds"
          classSessionUpdate[oldSessionId] = {
            [sessionIdField]: arrayRemove(studentId),
          }
          classSessionUpdate[newSessionId] = {
            [sessionIdField]: arrayUnion(studentId),
          }
        })

        // Check if old class has any remaining enrolled sessions
        const hasRemainingEnrollment = isStillEnrolled(
          clonedAttendance[oldClassId]
        )

        // If no remaining enrollment, delete the old class attendance record
        if (!hasRemainingEnrollment) {
          delete clonedAttendance[oldClassId]
        }

        // Update class studentIds or assistantIds based on isAssistant
        const classIdField = isAssistant ? "assistantIds" : "studentIds"
        const classUpdate: Record<string, FirebaseBatchUpdateClass> = {}

        // Remove student from old class if no longer enrolled
        if (!hasRemainingEnrollment) {
          classUpdate[oldClassId] = {
            [classIdField]: arrayRemove(studentId),
          }
        }

        // Add student to new class
        classUpdate[newClassId] = {
          [classIdField]: arrayUnion(studentId),
        }

        const studentAttendanceField = isAssistant
          ? "assistantAttendance"
          : "attendance"
        const update: FirebaseBatchUpdate = {
          [DOC_TYPES.STUDENTS]: {
            [studentId]: {
              [studentAttendanceField]: clonedAttendance,
              ...skillLevelPatch,
            },
          },
          [DOC_TYPES.CLASS_SESSIONS]: classSessionUpdate,
          [DOC_TYPES.CLASSES]: classUpdate,
        }

        await onSwitchClass(update)
        message.success(
          `${isAssistant ? "Assistant attendance" : "Class"} switched successfully.`
        )

        // No receipts for assistant swaps
        if (isAssistant) {
          return
        }

        if (!newClassObj || !oldClassObj || !student) {
          throw new Error("New class not found. Can't send receipt.")
        }

        // Find first and last selected indices for receipt dates
        let firstIndex = -1
        let lastIndex = -1
        for (let i = 0; i < selectedIndices.length; i++) {
          if (selectedIndices[i]) {
            if (firstIndex === -1) firstIndex = i
            lastIndex = i
          }
        }

        const oldClassLastDate =
          firstIndex > 0
            ? sortedOldClassSessions[firstIndex - 1]?.eventStart
            : sortedOldClassSessions[0]?.eventStart
        const firstSwitchedSessionDate =
          sortedNewClassSessions[firstIndex]?.eventStart
        const lastSwitchedSessionDate =
          sortedNewClassSessions[lastIndex]?.eventStart ||
          last(sortedNewClassSessions)?.eventStart

        const receipt: SwitchClassReceipt = {
          recipients: getStudentRecipients(student),
          sendTime: dayjs().format(STANDARD_DATE_TIME_FORMAT),

          DOB: formatDob(student.dob) ?? UNKNOWN_DOB,
          Name: student.name,

          OldClassStartTime: oldClassObj.startTime,
          OldClassEndTime: oldClassObj.endTime,
          OldClassDay: dayjs(oldClassObj.startDate).format(STANDARD_DAY_FORMAT),
          OldClassLastDate:
            dayjs(oldClassLastDate).format(STANDARD_DATE_FORMAT),

          NewClassStartTime: newClassObj.startTime,
          NewClassEndTime: newClassObj.endTime,
          NewClassDay: dayjs(newClassObj.startDate).format(STANDARD_DAY_FORMAT),
          NewClassFirstDate: dayjs(firstSwitchedSessionDate).format(
            STANDARD_DATE_FORMAT
          ),
          NewClassLastDate: dayjs(lastSwitchedSessionDate).format(
            STANDARD_DATE_FORMAT
          ),
        }

        modal.confirm({
          title:
            "Class switched successfully! Would you like to send a confirmation email?",
          okText: "Yes, send email",
          cancelText: "No",
          onOk: async () => {
            await apiHandler.sendReceiptEmail(
              receipt,
              ENROLLMENT_RECEIPT_TYPES.SWITCH_CLASS
            )
            message.success("Switch class confirmation email sent")
          },
        })
      } catch (e) {
        openErrorModal(
          e as ErrorType,
          `Switch ${isAssistant ? "assistant class" : "class"} button\n\n${isAssistant ? "assistant" : "student"} ${student?.name} (${studentId})`
        )
      }
    },
    [
      selectedIndices,
      modal,
      apiHandler,
      student,
      isAssistant,
      oldClassId,
      openErrorModal,
      store,
      studentId,
      onSwitchClass,
      message,
      oldClassObj,
    ]
  )

  // Expose submit method to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      submit: async () => {
        // If no sessions selected, show confirmation to close without changes
        const selectedCount = selectedIndices.filter(Boolean).length
        if (selectedCount === 0) {
          return new Promise<void>((resolve, reject) => {
            modal.confirm({
              title: "No sessions selected",
              content:
                "You haven't selected any sessions to switch. Close without making changes?",
              onOk: () => {
                resolve()
              },
              // This exact message prevents logging this as an error in ButtonToggleModal
              onCancel: () => reject(new Error("Cancelling closing form")),
              okText: "Yes",
              cancelText: "Cancel",
            })
          })
        }
        await switchClassForm.submit()
      },
    }),
    [modal, selectedIndices, switchClassForm]
  )

  return (
    <FormWithValues
      form={switchClassForm}
      onFinish={handleSubmit}
      layout="vertical"
      clearOnDestroy
      initialValues={{
        switchDate: getNow().startOf("day"),
      }}
    >
      <SwitchClassModalContent
        oldClassId={oldClassId}
        studentId={studentId}
        onSelectionChange={setSelectedIndices}
        isAssistant={isAssistant}
      />
    </FormWithValues>
  )
})

SwitchClassModal.displayName = "SwitchClassModal"

export default SwitchClassModal
